import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, Users } from 'lucide-react';
import { ErrorState, KpiCard, PageHeader, Toolbar } from '../../components/app';
import { useExpenses } from './hooks/useExpenses';
import { ExpensesTable, ExpenseDialog, PayersDialog } from './components';

const NUMBER = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const formatMoney = (amount: number) => `${NUMBER.format(amount)} сум`;

/** 1 запись, 2 записи, 5 записей. */
function plural(n: number) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'запись';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'записи';
  return 'записей';
}

export default function ExpensesPage() {
  const {
    expenses,
    summary,
    payers,
    isLoading,
    loadError,
    retryLoad,
    isCreateDialogOpen,
    isEditDialogOpen,
    isPayersDialogOpen,
    formData,
    newPayerName,
    isSubmitting,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setIsPayersDialogOpen,
    setFormData,
    setNewPayerName,
    openCreate,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleAddPayer,
    handleDeletePayer,
  } = useExpenses();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Расходы"
        subtitle="Траты на дело и кто их оплатил: Основатель или Счёт компании"
        actions={
          <>
            <Button variant="secondary" onClick={() => setIsPayersDialogOpen(true)}>
              <Users aria-hidden />
              Плательщики
            </Button>
            <Button onClick={openCreate}>
              <Plus aria-hidden />
              Добавить расход
            </Button>
          </>
        }
      />

      {!loadError && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <KpiCard
            variant="hero"
            label="Всего потрачено"
            value={NUMBER.format(summary.total)}
            unit="сум"
            hint={`${expenses.length} ${plural(expenses.length)}`}
            loading={isLoading}
          />
          <Card className="flex flex-col gap-3 px-[18px] py-4 md:col-span-2">
            <span className="text-[13px] text-text-2">По плательщикам</span>
            {isLoading ? (
              <div className="flex gap-6">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-32" />
              </div>
            ) : summary.byPayer.length === 0 ? (
              <span className="text-sm text-text-2">Нет данных</span>
            ) : (
              <dl className="m-0 flex flex-wrap gap-x-8 gap-y-3">
                {summary.byPayer.map((row) => (
                  <div key={row.payerId} className="flex flex-col gap-0.5">
                    <dt className="text-[13px] font-semibold text-text-2">{row.payerName}</dt>
                    <dd className="m-0 font-display text-lg font-bold tabular-nums">{formatMoney(row.total)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Card>
        </div>
      )}

      <Card className="overflow-hidden">
        {loadError ? (
          <ErrorState title="Не удалось загрузить расходы" message={loadError} onRetry={retryLoad} />
        ) : (
          <>
            <Toolbar title={isLoading ? 'Расходы' : `Найдено ${expenses.length}`} />
            <ExpensesTable
              expenses={expenses}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              formatMoney={formatMoney}
            />
          </>
        )}
      </Card>

      {/* Dialogs */}
      <ExpenseDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        payers={payers}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        isSubmitting={isSubmitting}
        title="Новый расход"
        description="Добавьте расход и укажите, кто платил"
        submitLabel="Добавить"
      />
      <ExpenseDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        payers={payers}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
        title="Редактировать расход"
        description="Обновите данные расхода"
        submitLabel="Сохранить"
      />
      <PayersDialog
        isOpen={isPayersDialogOpen}
        onOpenChange={setIsPayersDialogOpen}
        payers={payers}
        newPayerName={newPayerName}
        onNewPayerNameChange={setNewPayerName}
        onAddPayer={handleAddPayer}
        onDeletePayer={handleDeletePayer}
      />
    </div>
  );
}
